import { useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { mountFooter } from "@/shared/footer-motion";
import { SocialIcon } from "@/shared/social-icon.mjs";
import "@/shared/footer-motion.css";
import KLogo from "@/components/KLogo";
import { footerCols, footerProducts, company } from "@/data/nav.mjs";
import { socialLinks } from "@/data/social.mjs";

// Columns, products and the legal identity come from src/data/nav.mjs, the
// single source shared with the static Library shell.
const cols = footerCols;

/** Shared native curtain reveal; touch, short pages and reduced motion use flow. */
export default function Footer() {
  const footRef = useRef(null);
  const { pathname } = useLocation();
  useLayoutEffect(() => mountFooter(footRef.current), [pathname]);

  const inner = (
    <div className="kb-footer-inner max-w-7xl mx-auto px-6 md:px-12 py-20">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <span className="flex items-center gap-3">
            <KLogo className="h-10 w-10" />
            <span className="font-heading font-black text-3xl tracking-normal text-white">
              KED<span className="text-cyan-accent">BYTE</span>
            </span>
          </span>
          <p className="mt-5 text-zinc-400 max-w-sm leading-relaxed">
            An ultra-minimal, futuristic software studio — building intelligent,
            high-performance digital products from interface to infrastructure.
          </p>
          <a
            href="mailto:techteam@kedbyte.com"
            className="mt-6 inline-flex items-center gap-1.5 text-white hover:text-cyan-accent transition-colors"
            data-testid="footer-email"
          >
            techteam@kedbyte.com <ArrowUpRight className="h-4 w-4" />
          </a>
          <nav aria-label="KedByte social profiles" className="mt-5">
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
              {socialLinks.map(({ label, href, icon }) => (
                <li key={href}>
                  <a
                    href={href}
                    aria-label={`KedByte on ${label}`}
                    className="kb-social-link inline-flex min-h-[44px] items-center gap-2 rounded-sm text-sm text-zinc-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-accent"
                  >
                    <SocialIcon name={icon} />
                    <span>{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {cols.map((c) => (
          <div key={c.title} className="md:col-span-2">
            <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 mb-5">
              {c.title}
            </h2>
            <ul className="space-y-3">
              {c.links.map((l, i) => (
                <li key={i}>
                  <Link
                    to={l.to}
                    className="text-zinc-400 hover:text-white transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="md:col-span-2">
          <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 mb-5">
            Products
          </h2>
          <ul className="space-y-3">
            {footerProducts.map((p) =>
              p.isStatic ? (
                // Pre-rendered page, not a router route — see nav.mjs.
                <li key={p.to}>
                  <a
                    href={p.to}
                    className="text-zinc-400 hover:text-white transition-colors text-sm"
                    data-testid={p.testid}
                  >
                    {p.label}
                  </a>
                </li>
              ) : (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    className="text-zinc-400 hover:text-white transition-colors text-sm"
                    data-testid={p.testid}
                  >
                    {p.label}
                  </Link>
                </li>
              )
            )}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 mb-5">
            Registered
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed">
            {company.name}
            <br />
            {company.city}, {company.region}, {company.country}
          </p>
        </div>
      </div>

      {/* Original whole-word scaleX assembly, delayed until actually visible.
          Transforms never change letter spacing or move the footer's links. */}
      {/* data-decorative: WCAG 1.4.3 "pure decoration" exemption — excluded from axe scan */}
      <div className="mt-16 overflow-hidden" aria-hidden data-decorative="true">
        <div
          className="text-center font-heading font-black uppercase leading-none text-[13vw] md:text-[10vw] text-white/[0.06] select-none whitespace-nowrap tracking-[0.02em]"
        >
          KEDBYTE
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-zinc-400 font-mono">
        <span>© {new Date().getFullYear()} {company.name}. All rights reserved.</span>
        <span>Incorporated {company.incorporated} · {company.city}, {company.region}.</span>
      </div>
    </div>
  );

  return (
    <footer ref={footRef} className="border-t border-white/10 bg-ink" data-testid="site-footer">
      {inner}
    </footer>
  );
}
